/**
 * Checkout Module
 *
 * Creates invoice via backend API and redirects to hosted checkout page.
 * All checkout flows go through: checkout.shoppex.io/invoice/{invoiceId}
 */

import { getConfig } from '../core/config';
import { post } from '../core/client';
import { getCart, getCartCoupon, getCartCouponSource, createCartBackup, clearCart, getLatestQuoteToken } from './cart';
import { getAffiliateCode, trackAffiliateEvent } from './affiliates';
import type { CartItem, CartAddon } from '../types/cart';
import type { BuyerRewards } from '../types/api';
import {
  findRemainingProductRedirectPlaceholders,
  isSafeHttpsRedirectTemplateUrl,
} from '@shoppex/contracts/redirect-link-template';
import {
  normalizeRequestedCurrency,
  resolveBuyerCurrency,
} from '../utils/requested-currency';

export interface CheckoutOptions {
  autoRedirect?: boolean;
  locale?: string;
  email?: string;
  coupon?: string;
  currency?: string;
  /**
   * Absolute HTTPS URL used after a completed purchase when the product does
   * not define its own redirect. This is persisted on the invoice, so each
   * sales channel can provide its own return destination.
   */
  redirectUrl?: string;
  /**
   * Pin the quote proof this hand-off submits, as a tri-state: `undefined`
   * lets the SDK read the latest settled quote from storage, `null` submits
   * explicitly none, and a string submits exactly that token.
   */
  quoteToken?: string | null;
  /**
   * Referral (affiliate) code to submit, as a tri-state:
   *
   * - `undefined` — the SDK resolves it from storage (cart-applied referral
   *   code first, then the ambient `?ref=` attribution).
   * - `''` — explicitly none: submit no referral code and do NOT re-read
   *   storage. This is how a caller pins "quoted WITHOUT a referral"; without
   *   it a code captured between the quote and the submit would be applied to
   *   a total the buyer never saw.
   * - non-empty — pinned: submit exactly this code.
   *
   * `referralCode` is an alias and follows the same rules; `affiliateCode`
   * wins when both are present.
   */
  affiliateCode?: string;
  /** Alias of {@link CheckoutOptions.affiliateCode}, same tri-state. */
  referralCode?: string;
  customerCheckoutPath?: '/dashboard/api/checkout';
  /**
   * The cart this hand-off was approved FOR, as `JSON.stringify(getCart())`.
   *
   * PAYMENT PATH. A caller that writes the cart and reads it back before
   * checking out (Buy Now, which replaces the cart, is the sharp case) verifies
   * a cart it read at one moment, while these functions read storage again on
   * their way into the request — and localStorage is shared with every other
   * tab on the domain. A write landing in that gap is billed without ever
   * having passed the caller's verification.
   *
   * The caller cannot close that itself: any check it makes is one more read
   * BEFORE this one. Pinning the expected bytes moves the comparison to the
   * only place it can be conclusive — against the exact cart the call is about
   * to POST. A mismatch refuses before anything is created or sent.
   *
   * Honoured identically by BOTH invoice-creating entry points, `checkout()`
   * and `buildCheckoutUrl()` — they hit the same endpoint and create the same
   * invoice, so a pin that held on one and not the other would be a promise
   * the caller could not rely on. Only the refusal shape differs, following
   * each function's own success shape: `checkout()` returns
   * `{ success: false }`, `buildCheckoutUrl()` throws.
   *
   * Optional and additive: omitted, nothing changes, and the server is not
   * involved either way.
   */
  expectedCart?: string;
}

export interface CheckoutResult {
  success: boolean;
  redirectUrl?: string;
  invoiceId?: string;
  rewards?: BuyerRewards | null;
  message?: string;
  /**
   * The server's machine-readable refusal identifier when the checkout was
   * refused, e.g. `errors.checkout.price_increased_since_quote` or
   * `errors.checkout.affiliate_code_not_applicable`. Branch on this rather
   * than on `message`, which is localized display copy.
   *
   * The quote-proof family — `errors.checkout.quote_token_invalid`,
   * `errors.checkout.quote_token_expired`, `errors.checkout.quote_token_stale`
   * — says the proof this call submitted could not be honoured: not ours, past
   * its window, or priced for a cart/currency/codes that have since moved. The
   * server refuses rather than pricing without the ceiling, so the caller's
   * response is always the same: `quoteCart()` again, show the buyer the new
   * total, and only then retry. A retry with the same stale token is refused
   * identically.
   *
   * Absent for failures the server never named (transport, timeout, an empty
   * cart caught client-side).
   */
  code?: string;
}

/**
 * Typed refusal from {@link buildCheckoutUrl}. The function keeps its existing
 * throw-based contract while preserving the server error code and status.
 */
export class CheckoutCreateError extends Error {
  readonly code?: string;
  readonly status?: number;

  constructor(message: string, options: {
    code?: string;
    status?: number;
  } = {}) {
    super(message);
    this.name = 'CheckoutCreateError';
    this.code = options.code;
    this.status = options.status;
    Object.setPrototypeOf(this, CheckoutCreateError.prototype);
  }
}

interface CheckoutApiResponse {
  invoiceId?: string;
  checkoutUrl?: string;
  invoice_id?: string;
  checkout_url?: string;
  uniqid?: string;
  url?: string;
  url_branded?: string;
  rewards?: BuyerRewards | null;
  invoice?: {
    invoiceId?: string;
    checkoutUrl?: string;
    invoice_id?: string;
    checkout_url?: string;
    uniqid?: string;
    url?: string;
    url_branded?: string;
    rewards?: BuyerRewards | null;
  };
}

interface NormalizedCheckoutData {
  invoiceId: string;
  checkoutUrl: string;
  rewards: BuyerRewards | null;
}

const CHECKOUT_PREFILL_EMAIL_HASH_KEY = 'shoppex_prefill_email';

/**
 * PAYMENT PATH. The single refusal copy for a broken `expectedCart` pin, shared
 * by every entry point that honours the pin so the buyer reads the same
 * sentence whichever one the theme calls. One literal, because two copies drift
 * and the difference would be visible to buyers on the same storefront.
 */
const CART_CHANGED_MESSAGE = 'Your cart changed while checkout was starting. Please review it and try again.';

/**
 * PAYMENT PATH. True when the caller pinned a cart and storage no longer holds
 * it — see `CheckoutOptions.expectedCart`. Compared against the exact array the
 * caller is about to bill, which is the only comparison that is conclusive:
 * anything the caller checks itself is one more read BEFORE the read that
 * feeds the request.
 *
 * An omitted pin is not a mismatch. The pin is additive, and a caller that
 * never made the promise is left exactly as it was.
 */
function isExpectedCartMismatch(cart: CartItem[], expectedCart: string | undefined): boolean {
  return expectedCart !== undefined && JSON.stringify(cart) !== expectedCart;
}

function normalizeCoupon(coupon: string | null | undefined): string | null {
  const normalized = coupon?.trim();
  return normalized ? normalized : null;
}

function normalizeEmail(email: string | null | undefined): string | null {
  const normalized = email?.trim();
  return normalized ? normalized : null;
}

function resolvePostPurchaseRedirectUrl(
  redirectUrl: string | null | undefined,
): { value: string | null; error: null } | { value: null; error: string } {
  const normalized = redirectUrl?.trim();
  if (!normalized) {
    return { value: null, error: null };
  }
  if (!isSafeHttpsRedirectTemplateUrl(normalized)) {
    return { value: null, error: 'redirectUrl must be an absolute HTTPS URL.' };
  }
  if (findRemainingProductRedirectPlaceholders(normalized).length > 0) {
    return { value: null, error: 'redirectUrl must not contain template placeholders.' };
  }
  return { value: normalized, error: null };
}

interface PendingCheckoutCreate {
  fingerprint: string;
  key: string;
}

const pendingCheckoutCreates = new Map<string, PendingCheckoutCreate>();

function acquireCheckoutCreateIdempotency(
  requestTarget: { endpoint: string; baseUrl?: string },
  createIntent: object,
): PendingCheckoutCreate {
  const fingerprint = JSON.stringify({
    endpoint: requestTarget.endpoint,
    baseUrl: requestTarget.baseUrl ?? null,
    createIntent,
  });
  const existingAttempt = pendingCheckoutCreates.get(fingerprint);
  if (existingAttempt) {
    return existingAttempt;
  }

  const attempt = {
    fingerprint,
    key: globalThis.crypto.randomUUID(),
  };
  pendingCheckoutCreates.set(fingerprint, attempt);
  return attempt;
}

function releaseCheckoutCreateIdempotency(
  attempt: PendingCheckoutCreate,
  outcomeDefinitive: boolean,
): void {
  // Only a parsed server refusal or a validated success makes the outcome
  // definitive. A rejected fetch, timeout, truncated body, or malformed body
  // may follow a committed create, so the next identical intent replays it.
  // An attempt is identified by its key, not object identity, so a late
  // definitive outcome cannot release a newer attempt for the same intent.
  if (outcomeDefinitive && pendingCheckoutCreates.get(attempt.fingerprint)?.key === attempt.key) {
    pendingCheckoutCreates.delete(attempt.fingerprint);
  }
}

function resolveCustomerCheckoutRequestTarget(options: CheckoutOptions): {
  endpoint: string;
  baseUrl?: string;
} {
  if (
    options.customerCheckoutPath === '/dashboard/api/checkout'
    && typeof window !== 'undefined'
    && typeof window.location?.origin === 'string'
  ) {
    return {
      endpoint: options.customerCheckoutPath,
      baseUrl: window.location.origin,
    };
  }

  return { endpoint: '/v1/storefront/invoices/from-cart' };
}

function resolveRequestedCheckoutCurrency(options: CheckoutOptions): string | null {
  // An EXPLICIT currency option pins the request outright — '' means "the
  // server default", and neither falls through to the location/config reads.
  if (options.currency !== undefined) {
    return normalizeRequestedCurrency(options.currency);
  }
  return resolveBuyerCurrency(getConfig().currency);
}

/**
 * The currency an option-less checkout would request right now (location,
 * then config).
 */
export function getRequestedCheckoutCurrency(): string | null {
  return resolveBuyerCurrency(getConfig().currency);
}

function normalizeCheckoutFailureMessage(rawMessage: string | null | undefined): string {
  const message = rawMessage?.trim() ?? '';
  if (!message) {
    return 'Checkout failed. Please try again.';
  }

  if (isStaleCartProductError(message)) {
    return 'Your cart is outdated. Please add the products again.';
  }

  const httpMatch = message.match(/^HTTP\s+(\d{3})(?::\s*(.*))?$/i);
  if (httpMatch) {
    const status = Number(httpMatch[1]);
    const detail = httpMatch[2]?.trim();

    if (detail && detail.length > 0) {
      return `Checkout failed: ${detail}`;
    }

    if (status >= 500) {
      return 'Checkout is temporarily unavailable. Please try again.';
    }

    if (status === 400) {
      return 'Checkout failed. Please check your details and try again.';
    }

    if (status === 401 || status === 403) {
      return 'Checkout is currently unavailable for this request.';
    }

    return 'Checkout failed. Please try again.';
  }

  if (/^internal server error$/i.test(message)) {
    return 'Checkout is temporarily unavailable. Please try again.';
  }

  return message;
}

function isStaleCartProductError(rawMessage: string | null | undefined): boolean {
  const message = rawMessage?.trim().toLowerCase() ?? '';
  if (!message) {
    return false;
  }

  return message.includes('product not found')
    || message.includes('product not available')
    || message.includes('products are no longer available')
    || message.includes('outdated product');
}

function validateCheckoutUrl(
  checkoutUrl: string,
  checkoutBaseUrl: string | undefined,
  expectedInvoiceId?: string
): string | null {
  const expectedBaseUrl = checkoutBaseUrl?.trim();
  if (!expectedBaseUrl) {
    return null;
  }

  try {
    const parsedCheckoutUrl = new URL(checkoutUrl);
    const parsedExpectedBaseUrl = new URL(expectedBaseUrl);

    if (parsedCheckoutUrl.origin !== parsedExpectedBaseUrl.origin) {
      return null;
    }

    const normalizedPath = parsedCheckoutUrl.pathname.replace(/\/+$/, '');
    const normalizedBasePath = parsedExpectedBaseUrl.pathname.replace(/\/+$/, '');
    const expectedInvoicePath = `${normalizedBasePath}/invoice/`.replace(/\/{2,}/g, '/');
    if (!normalizedPath.startsWith(expectedInvoicePath)) {
      return null;
    }

    const invoiceIdSegment = normalizedPath.slice(expectedInvoicePath.length);
    if (!invoiceIdSegment || invoiceIdSegment.includes('/')) {
      return null;
    }

    if (expectedInvoiceId) {
      const normalizedExpectedInvoiceId = expectedInvoiceId.trim();
      const invoiceIdFromUrl = decodeURIComponent(invoiceIdSegment);
      if (!normalizedExpectedInvoiceId || invoiceIdFromUrl !== normalizedExpectedInvoiceId) {
        return null;
      }
    }

    return parsedCheckoutUrl.toString();
  } catch {
    return null;
  }
}

function buildCheckoutUrlFromInvoiceId(
  checkoutBaseUrl: string | undefined,
  invoiceId: string
): string | null {
  const normalizedBaseUrl = checkoutBaseUrl?.trim();
  if (!normalizedBaseUrl) {
    return null;
  }

  try {
    const baseUrl = new URL(normalizedBaseUrl);
    const basePath = baseUrl.pathname.replace(/\/+$/, '');
    baseUrl.pathname = `${basePath}/invoice/${encodeURIComponent(invoiceId)}`.replace(/\/{2,}/g, '/');
    baseUrl.search = '';
    baseUrl.hash = '';
    return baseUrl.toString();
  } catch {
    return null;
  }
}

function appendCheckoutUrlOptions(
  checkoutUrl: string,
  options: { email?: string | null; locale?: string },
): string {
  const normalizedEmail = normalizeEmail(options.email);
  const normalizedLocale = typeof options.locale === 'string' ? options.locale.trim() : '';

  if (!normalizedEmail && !normalizedLocale) {
    return checkoutUrl;
  }

  try {
    const parsedCheckoutUrl = new URL(checkoutUrl);
    if (normalizedLocale) {
      parsedCheckoutUrl.searchParams.set('locale', normalizedLocale);
    }
    if (normalizedEmail) {
      const hashParams = new URLSearchParams(parsedCheckoutUrl.hash.startsWith('#')
        ? parsedCheckoutUrl.hash.slice(1)
        : parsedCheckoutUrl.hash);
      hashParams.set(CHECKOUT_PREFILL_EMAIL_HASH_KEY, normalizedEmail);
      parsedCheckoutUrl.hash = hashParams.toString();
    }
    return parsedCheckoutUrl.toString();
  } catch {
    return checkoutUrl;
  }
}

function normalizeCheckoutResponse(
  response: CheckoutApiResponse | undefined,
  checkoutBaseUrl: string | undefined
): NormalizedCheckoutData | null {
  const nestedInvoice = response?.invoice;
  const invoiceId = response?.invoiceId?.trim()
    || response?.invoice_id?.trim()
    || response?.uniqid?.trim()
    || nestedInvoice?.invoiceId?.trim()
    || nestedInvoice?.invoice_id?.trim()
    || nestedInvoice?.uniqid?.trim();
  let checkoutUrl = response?.checkoutUrl?.trim()
    || response?.checkout_url?.trim()
    || response?.url_branded?.trim()
    || response?.url?.trim()
    || nestedInvoice?.checkoutUrl?.trim()
    || nestedInvoice?.checkout_url?.trim()
    || nestedInvoice?.url_branded?.trim()
    || nestedInvoice?.url?.trim();

  if (!checkoutUrl && invoiceId && nestedInvoice) {
    checkoutUrl = buildCheckoutUrlFromInvoiceId(checkoutBaseUrl, invoiceId) ?? undefined;
  }

  if (!invoiceId || !checkoutUrl) {
    return null;
  }

  return {
    invoiceId,
    checkoutUrl,
    rewards: response?.rewards ?? nestedInvoice?.rewards ?? null,
  };
}

function resolveCheckoutOptions(
  couponOrOptions?: string | CheckoutOptions,
  options?: CheckoutOptions
): CheckoutOptions {
  if (typeof couponOrOptions === 'string') {
    return {
      ...options,
      coupon: couponOrOptions,
    };
  }

  return couponOrOptions ?? options ?? {};
}

function normalizeAffiliateCode(options: CheckoutOptions, storedAffiliateCode: string | null): string | null {
  const explicitCode = options.affiliateCode ?? options.referralCode;
  // Tri-state, mirroring `coupon`: an explicit empty string means "none" and
  // must suppress the storage re-read. Only `undefined` lets the SDK resolve
  // the code itself — otherwise a referral captured after the quote was
  // rendered would be applied to a total the buyer never saw.
  if (typeof explicitCode === 'string') {
    const normalized = explicitCode.trim().toLowerCase();
    return normalized.length > 0 ? normalized : null;
  }

  return storedAffiliateCode?.trim().toLowerCase() ?? getAffiliateCode();
}

function resolveCheckoutCodes(options: CheckoutOptions): {
  coupon: string | null;
  affiliateCode: string | null;
} {
  const storedCode = getCartCoupon();
  const storedSource = getCartCouponSource();
  const coupon = options.coupon === undefined
    ? (storedSource === 'COUPON' ? storedCode : null)
    : normalizeCoupon(options.coupon);
  const storedAffiliateCode = options.coupon === undefined && storedSource === 'AFFILIATE'
    ? storedCode
    : null;

  return {
    coupon,
    affiliateCode: normalizeAffiliateCode(options, storedAffiliateCode),
  };
}

function mapCartItemsForApi(items: CartItem[]) {
  const normalizeVariantIdForApi = (value: string | null | undefined): string | null => {
    const normalized = value?.trim();
    if (!normalized) return null;
    // Themes use "default" as a sentinel for "no variant selected".
    // Backend expects `null` in that case.
    if (normalized.toLowerCase() === 'default') return null;
    return normalized;
  };

  return items.map((item) => ({
    product_id: item.product_id,
    variant_id: normalizeVariantIdForApi(item.variant_id),
    quantity: item.quantity,
    addons: item.addons?.map((a: CartAddon) => ({ id: a.id, quantity: a.quantity ?? 1 })),
    custom_fields: item.custom_fields,
    price_variant_id: item.price_variant_id || null,
    pay_what_you_want_price: item.pay_what_you_want_price,
  }));
}

export async function checkout(
  couponOrOptions?: string | CheckoutOptions,
  options?: CheckoutOptions
): Promise<CheckoutResult> {
  const resolvedOptions = resolveCheckoutOptions(couponOrOptions, options);
  return performCheckout(resolvedOptions);
}

async function performCheckout(
  resolvedOptions: CheckoutOptions,
): Promise<CheckoutResult> {
  const { autoRedirect = true, email } = resolvedOptions;
  const checkoutCodes = resolveCheckoutCodes(resolvedOptions);
  const normalizedCoupon = checkoutCodes.coupon;
  const normalizedEmail = normalizeEmail(email);
  const normalizedAffiliateCode = checkoutCodes.affiliateCode;
  const requestedCurrency = resolveRequestedCheckoutCurrency(resolvedOptions);
  const postPurchaseRedirect = resolvePostPurchaseRedirectUrl(resolvedOptions.redirectUrl);

  const cart = getCart();
  if (cart.length === 0) {
    return {
      success: false,
      message: 'Cart is empty',
    };
  }

  // PAYMENT PATH. The cart the caller approved, compared against the cart this
  // call will actually POST — see `CheckoutOptions.expectedCart`. Refused
  // before the backup and before the request, so a cart that changed under the
  // caller is never billed and nothing is left half-done.
  if (isExpectedCartMismatch(cart, resolvedOptions.expectedCart)) {
    return {
      success: false,
      message: CART_CHANGED_MESSAGE,
    };
  }
  if (postPurchaseRedirect.error) {
    return {
      success: false,
      message: postPurchaseRedirect.error,
    };
  }

  createCartBackup();

  const config = getConfig();
  const checkoutRequestTarget = resolveCustomerCheckoutRequestTarget(resolvedOptions);

  const createIntent = {
    shop_slug: config.storeSlug,
    cart: mapCartItemsForApi(cart),
    email: normalizedEmail,
    coupon: normalizedCoupon,
    currency: requestedCurrency,
    return_url: postPurchaseRedirect.value ?? undefined,
    affiliate_code: normalizedAffiliateCode,
    // Proof of the total the buyer approved, from the last quoteCart(). Sent
    // automatically so the server can refuse an invoice priced above it.
    // Undefined when nothing has been quoted this session — the endpoint
    // treats that exactly as an older SDK.
    quote_token: resolvedOptions.quoteToken !== undefined
      ? (resolvedOptions.quoteToken ?? undefined)
      : (getLatestQuoteToken() ?? undefined),
  };
  const createAttempt = acquireCheckoutCreateIdempotency(checkoutRequestTarget, createIntent);
  const response = await post<CheckoutApiResponse>(
    checkoutRequestTarget.endpoint,
    createIntent,
    {
      retries: 0,
      baseUrl: checkoutRequestTarget.baseUrl,
      headers: {
        'X-Idempotency-Key': createAttempt.key,
      },
    }
  );
  if (!response.success || !response.data) {
    releaseCheckoutCreateIdempotency(createAttempt, response.responseDefinitive === true);
    if (isStaleCartProductError(response.message)) {
      clearCart();
    }
    return {
      success: false,
      message: normalizeCheckoutFailureMessage(response.message),
      // The server's machine-readable refusal, preserved so callers can react
      // to e.g. `errors.checkout.price_increased_since_quote` without matching
      // localized copy.
      ...(response.code ? { code: response.code } : {}),
    };
  }

  const checkoutData = normalizeCheckoutResponse(response.data, config.checkoutBaseUrl);
  if (!checkoutData) {
    return {
      success: false,
      message: 'Failed to create invoice',
    };
  }

  const { invoiceId } = checkoutData;
  const safeCheckoutUrl = validateCheckoutUrl(
    checkoutData.checkoutUrl,
    config.checkoutBaseUrl,
    invoiceId
  );
  if (!safeCheckoutUrl) {
    return {
      success: false,
      message: 'Failed to create invoice',
    };
  }

  releaseCheckoutCreateIdempotency(createAttempt, true);

  // One checkout_started per created invoice: dedupes accidental duplicate
  // sends without dropping a buyer's genuine second order in the same session.
  void trackAffiliateEvent('checkout_started', {
    code: normalizedAffiliateCode,
    dedupeKey: `inv:${invoiceId}`,
  });

  const checkoutUrlWithPrefill = appendCheckoutUrlOptions(safeCheckoutUrl, {
    email: normalizedEmail,
    locale: resolvedOptions.locale ?? config.locale,
  });

  if (autoRedirect) {
    if (typeof window !== 'undefined' && window?.location) {
      window.location.href = checkoutUrlWithPrefill;
      clearCart();
    }
  }

  return {
    success: true,
    redirectUrl: checkoutUrlWithPrefill,
    invoiceId,
    rewards: checkoutData.rewards,
  };
}

/**
 * Build checkout URL by creating invoice first.
 * Returns the checkout URL for the created invoice.
 *
 * PAYMENT PATH. "Build a URL" names what this returns, not a lighter way to
 * price a cart: it POSTs the same invoice-creating endpoint as `checkout()` and
 * bills whatever storage holds when it reads. It therefore honours
 * `CheckoutOptions.expectedCart` on exactly the same terms — a pinned cart that
 * moved is refused, not invoiced.
 *
 * A refusal throws, like every other refusal on this function: the success
 * shape is a URL string, so there is no in-band value that a caller could
 * mistake for one. The thrown message is the buyer-facing copy, identical to
 * the sentence `checkout()` returns, and callers that already handle the
 * `Cart is empty` throw handle this one unchanged.
 */
export async function buildCheckoutUrl(
  couponOrOptions?: string | CheckoutOptions,
  options?: CheckoutOptions
): Promise<string> {
  const resolvedOptions = resolveCheckoutOptions(couponOrOptions, options);
  return performBuildCheckoutUrl(resolvedOptions);
}

async function performBuildCheckoutUrl(
  resolvedOptions: CheckoutOptions,
): Promise<string> {
  const { email } = resolvedOptions;
  const checkoutCodes = resolveCheckoutCodes(resolvedOptions);
  const normalizedCoupon = checkoutCodes.coupon;
  const normalizedEmail = normalizeEmail(email);
  const normalizedAffiliateCode = checkoutCodes.affiliateCode;
  const requestedCurrency = resolveRequestedCheckoutCurrency(resolvedOptions);
  const postPurchaseRedirect = resolvePostPurchaseRedirectUrl(resolvedOptions.redirectUrl);

  const cart = getCart();
  if (cart.length === 0) {
    throw new Error('Cart is empty');
  }

  // PAYMENT PATH. Same position `checkout()` puts it in: after the empty-cart
  // check and before anything is created or sent. This path has no cart backup
  // to write, so the request is the first side effect there is — refusing here
  // means a cart that changed under the caller never reaches the endpoint, and
  // the buyer's cart is left exactly as the interfering write left it. It
  // matters most with no quote token in play, because then nothing downstream
  // carries an approved-total ceiling either.
  if (isExpectedCartMismatch(cart, resolvedOptions.expectedCart)) {
    throw new Error(CART_CHANGED_MESSAGE);
  }
  if (postPurchaseRedirect.error) {
    throw new Error(postPurchaseRedirect.error);
  }

  const config = getConfig();
  const checkoutRequestTarget = resolveCustomerCheckoutRequestTarget(resolvedOptions);

  const createIntent = {
    shop_slug: config.storeSlug,
    cart: mapCartItemsForApi(cart),
    email: normalizedEmail,
    coupon: normalizedCoupon,
    currency: requestedCurrency,
    return_url: postPurchaseRedirect.value ?? undefined,
    affiliate_code: normalizedAffiliateCode,
    // Same invoice-creating endpoint as `checkout()`, so it carries the same
    // proof of the approved total. "Build a URL" describes what this returns
    // to the caller, not a different way to price a cart — omitting the
    // token here left a public path on which the server had nothing to check
    // the invoice against. Same optional semantics: undefined when nothing
    // was quoted this session.
    quote_token: resolvedOptions.quoteToken !== undefined
      ? (resolvedOptions.quoteToken ?? undefined)
      : (getLatestQuoteToken() ?? undefined),
  };
  const createAttempt = acquireCheckoutCreateIdempotency(checkoutRequestTarget, createIntent);
  const response = await post<CheckoutApiResponse>(
    checkoutRequestTarget.endpoint,
    createIntent,
    {
      retries: 0,
      baseUrl: checkoutRequestTarget.baseUrl,
      headers: {
        'X-Idempotency-Key': createAttempt.key,
      },
    }
  );
  if (!response.success || !response.data) {
    releaseCheckoutCreateIdempotency(createAttempt, response.responseDefinitive === true);
    if (isStaleCartProductError(response.message)) {
      clearCart();
    }
    throw new CheckoutCreateError(normalizeCheckoutFailureMessage(response.message), {
      ...(response.code ? { code: response.code } : {}),
      ...(response.status !== undefined ? { status: response.status } : {}),
    });
  }
  const checkoutData = normalizeCheckoutResponse(response.data, config.checkoutBaseUrl);
  if (!checkoutData) {
    throw new Error('Failed to create invoice');
  }

  const safeCheckoutUrl = validateCheckoutUrl(
    checkoutData.checkoutUrl,
    config.checkoutBaseUrl,
    checkoutData.invoiceId
  );
  if (!safeCheckoutUrl) {
    throw new Error('Failed to create invoice');
  }

  releaseCheckoutCreateIdempotency(createAttempt, true);

  // Same funnel point as checkout(): both entry points create the invoice.
  void trackAffiliateEvent('checkout_started', {
    code: normalizedAffiliateCode,
    dedupeKey: `inv:${checkoutData.invoiceId}`,
  });

  return appendCheckoutUrlOptions(safeCheckoutUrl, {
    email: normalizedEmail,
    locale: resolvedOptions.locale ?? config.locale,
  });
}

/**
 * @deprecated Use buildCheckoutUrl instead.
 * Sync version is no longer supported as invoice creation requires API call.
 */
export function buildCheckoutUrlSync(): never {
  throw new Error('buildCheckoutUrlSync is deprecated. Use buildCheckoutUrl (async) instead.');
}
